import { z } from 'zod';
import { SpaceRoleSchema } from './spaces.js';
import { DictColumnTypeSchema } from './dictionaries.js';
import { FieldTypeSchema } from './item-types.js';
import { JiraFieldMappingSchema, JiraPullMappingSchema, SyncDirectionSchema } from './jira.js';
import { JiraLinkDirectionSchema, MatchRuleSchema } from './link-rules.js';
import { HermesConfigSettingsSchema, HermesInputMappingSchema, HermesResultMappingSchema, } from './hermes.js';
/**
 * Документ настройки пространства (`docs/modules/mcp-jira.md`).
 *
 * Вся настройка пространства одним JSON: типы и поля, представления,
 * синхронизация с Jira, правила связей, оценка Гермеса, участники. Ссылки
 * внутри — по slug / названию / логину, а не по UUID, поэтому документ
 * переносится между пространствами и стендами.
 *
 * Правила применения:
 *  - раздела нет в документе — он не трогается;
 *  - элемента нет в списке — он не удаляется (удаление только через `delete`);
 *  - у элемента нет свойства — свойство не меняется.
 */
export const SPACE_CONFIG_VERSION = 1;
/** slug типа записи. Дефис допустим: так называются копии типов (`x-copy`). */
const ITEM_TYPE_SLUG_RE = /^[a-z][a-z0-9_-]{0,63}$/;
/**
 * Ссылка на тип записи: slug в этом же пространстве или
 * `{ space, itemType }` — тип в другом пространстве (правила связей и
 * колонки-суммы могут смотреть в чужое пространство).
 */
export const SpaceConfigItemTypeRefSchema = z.union([
    z.string().min(1),
    z.object({ space: z.string().min(1), itemType: z.string().min(1) }),
]);
/**
 * Ссылка на справочник: название. Если справочников с таким названием
 * несколько, выгрузка отдаёт `{ name, id }`, и при применении берётся id.
 */
export const SpaceConfigDictionaryRefSchema = z.union([
    z.string().min(1),
    z.object({ name: z.string().min(1), id: z.string().uuid().optional() }),
]);
// ─── space ────────────────────────────────────────────────────────────────
export const SpaceConfigSpaceSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().max(1000).nullable().optional(),
    icon: z.string().max(48).nullable().optional(),
    publicAccess: z.boolean().optional(),
    jiraIntegrationEnabled: z.boolean().optional(),
    hermesIntegrationEnabled: z.boolean().optional(),
    hermesCommonContext: z.string().max(5000).nullable().optional(),
});
// ─── dictionaries (справочно; создаются только отсутствующие) ─────────────
export const SpaceConfigDictionaryColumnSchema = z.object({
    slug: z.string().min(1),
    name: z.string().min(1),
    columnType: DictColumnTypeSchema,
    /** Для reference-колонки `config.dictionary` — ссылка на справочник. */
    config: z.record(z.unknown()).optional(),
});
export const SpaceConfigDictionarySchema = z.object({
    name: z.string().trim().min(1).max(200),
    /** Если справочников с таким названием несколько — какой именно. */
    id: z.string().uuid().optional(),
    description: z.string().max(1000).nullable().optional(),
    keyColumn: z.string().nullable().optional(),
    displayColumn: z.string().nullable().optional(),
    columns: z.array(SpaceConfigDictionaryColumnSchema).default([]),
    /** Только при создании нового справочника: начальные записи `{ columnSlug: value }`. */
    entries: z.array(z.record(z.unknown())).optional(),
});
// ─── fields ───────────────────────────────────────────────────────────────
export const SpaceConfigFieldSchema = z.object({
    slug: z.string().min(1),
    name: z.string().trim().min(1).max(200).optional(),
    fieldType: FieldTypeSchema.optional(),
    required: z.boolean().optional(),
    /** Для reference-поля `config.dictionary` вместо `dictionaryId`. */
    config: z.record(z.unknown()).optional(),
});
// ─── jira ─────────────────────────────────────────────────────────────────
/** Источник проекта / типа задачи Jira: поле записи указывается slug-ом. */
export const SpaceConfigJiraSourceSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('static') }),
    z.object({
        kind: z.literal('field'),
        field: z.string().min(1),
        valueMap: z.record(z.string(), z.string()).optional(),
    }),
]);
export const SpaceConfigSavedQuerySchema = z.object({
    name: z.string().trim().min(1).max(200),
    jql: z.string().min(1).max(2000),
    mode: z.enum(['create_only', 'create_and_update']).default('create_only'),
});
export const SpaceConfigJiraSchema = z.object({
    projectKey: z.string().min(1).optional(),
    issueType: z.string().min(1).optional(),
    syncDirection: SyncDirectionSchema.optional(),
    fieldMapping: JiraFieldMappingSchema.optional(),
    /** null — отдельной настройки загрузки нет, используется обратная к выгрузке. */
    pullFieldMapping: JiraPullMappingSchema.nullable().optional(),
    projectKeySource: SpaceConfigJiraSourceSchema.optional(),
    issueTypeSource: SpaceConfigJiraSourceSchema.optional(),
    displayFields: z.array(z.string()).optional(),
    /** Сохранённые запросы JQL; опознаются по name. */
    savedQueries: z.array(SpaceConfigSavedQuerySchema).optional(),
    /** Прочие ключи settings настройки синхронизации, как есть. */
    extraSettings: z.record(z.unknown()).optional(),
});
// ─── link rules ───────────────────────────────────────────────────────────
export const SpaceConfigLinkRuleSchema = z.object({
    target: SpaceConfigItemTypeRefSchema,
    linkType: z.string().trim().min(1).max(100),
    name: z.string().trim().min(1).max(200).optional(),
    jiraLinkDirection: JiraLinkDirectionSchema.optional(),
    matchRules: z.array(MatchRuleSchema).optional(),
    pushEnabled: z.boolean().optional(),
});
// ─── hermes ───────────────────────────────────────────────────────────────
export const SpaceConfigHermesSchema = z.object({
    /** Название стенда Гермеса. Ключи через документ не передаются. */
    stand: z.string().min(1).nullable().optional(),
    /** Собственный адрес — только для настроек без стенда (старая схема). */
    baseUrl: z.string().url().nullable().optional(),
    inputMapping: HermesInputMappingSchema.optional(),
    resultMapping: HermesResultMappingSchema.optional(),
    settings: HermesConfigSettingsSchema.partial().optional(),
});
// ─── item types ───────────────────────────────────────────────────────────
export const SpaceConfigItemTypeSchema = z.object({
    slug: z.string().trim().regex(ITEM_TYPE_SLUG_RE),
    name: z.string().trim().min(1).max(200).optional(),
    icon: z.string().max(10).nullable().optional(),
    fields: z.array(SpaceConfigFieldSchema).optional(),
    jira: SpaceConfigJiraSchema.optional(),
    linkRules: z.array(SpaceConfigLinkRuleSchema).optional(),
    hermes: SpaceConfigHermesSchema.optional(),
});
// ─── views ────────────────────────────────────────────────────────────────
export const SpaceConfigViewSchema = z.object({
    /** Представление опознаётся по названию. */
    name: z.string().trim().min(1).max(200),
    viewType: z.enum(['table', 'kanban']).optional(),
    itemTypes: z.array(SpaceConfigItemTypeRefSchema).min(1).optional(),
    icon: z.string().max(48).nullable().optional(),
    group: z.string().max(100).nullable().optional(),
    isDefault: z.boolean().optional(),
    /**
     * Конфиг как в API, но ссылки переносимые: `linkedItemTypes` (slug) вместо
     * `linkedItemTypeIds`, `linkRules` вместо `linkRuleIds`, значения фильтров по
     * справочнику — `{ entryKey }`, по пользователю — `{ login }`.
     */
    config: z.record(z.unknown()).optional(),
});
// ─── members ──────────────────────────────────────────────────────────────
export const SpaceConfigMemberSchema = z.object({
    login: z.string().min(1),
    role: SpaceRoleSchema,
});
// ─── document ─────────────────────────────────────────────────────────────
export const SpaceConfigDocumentSchema = z.object({
    version: z.literal(SPACE_CONFIG_VERSION).default(SPACE_CONFIG_VERSION),
    space: SpaceConfigSpaceSchema.optional(),
    dictionaries: z.array(SpaceConfigDictionarySchema).optional(),
    itemTypes: z.array(SpaceConfigItemTypeSchema).optional(),
    views: z.array(SpaceConfigViewSchema).optional(),
    members: z.array(SpaceConfigMemberSchema).optional(),
});
export const SPACE_CONFIG_SECTIONS = [
    'space',
    'dictionaries',
    'itemTypes',
    'fields',
    'jira',
    'linkRules',
    'hermes',
    'views',
    'members',
];
export const SpaceConfigSectionSchema = z.enum(SPACE_CONFIG_SECTIONS);
// ─── delete ───────────────────────────────────────────────────────────────
export const SpaceConfigDeleteSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('itemType'), itemType: z.string().min(1) }),
    z.object({ kind: z.literal('field'), itemType: z.string().min(1), slug: z.string().min(1) }),
    z.object({ kind: z.literal('view'), name: z.string().min(1) }),
    z.object({
        kind: z.literal('linkRule'),
        itemType: z.string().min(1),
        target: SpaceConfigItemTypeRefSchema,
        linkType: z.string().min(1),
    }),
    z.object({ kind: z.literal('jira'), itemType: z.string().min(1) }),
    z.object({ kind: z.literal('savedQuery'), itemType: z.string().min(1), name: z.string().min(1) }),
    z.object({ kind: z.literal('hermes'), itemType: z.string().min(1) }),
    z.object({ kind: z.literal('member'), login: z.string().min(1) }),
]);
// ─── plan / apply ─────────────────────────────────────────────────────────
export const SpaceConfigPlanRequestSchema = z.object({
    config: SpaceConfigDocumentSchema,
    delete: z.array(SpaceConfigDeleteSchema).max(500).optional(),
});
export const SpaceConfigApplyRequestSchema = SpaceConfigPlanRequestSchema.extend({
    planHash: z.string().min(1),
});
export const SPACE_CONFIG_CHANGE_KINDS = [
    'space',
    'dictionary',
    'itemType',
    'field',
    'jira',
    'linkRule',
    'hermes',
    'view',
    'member',
];
export const SpaceConfigChangeSchema = z.object({
    op: z.enum(['create', 'update', 'delete', 'reorder']),
    kind: z.enum(SPACE_CONFIG_CHANGE_KINDS),
    /** Человекочитаемый ключ: `field:employee_card.person`, `view:Карточки Q3`. */
    key: z.string(),
    diff: z
        .array(z.object({ path: z.string(), before: z.unknown(), after: z.unknown() }))
        .optional(),
});
export const SpaceConfigIssueSchema = z.object({
    key: z.string(),
    code: z.string(),
    message: z.string(),
});
export const SpaceConfigPlanResponseSchema = z.object({
    changes: z.array(SpaceConfigChangeSchema),
    warnings: z.array(SpaceConfigIssueSchema),
    errors: z.array(SpaceConfigIssueSchema),
    /** Передаётся в apply: если настройка в базе изменилась, apply вернёт 409. */
    planHash: z.string(),
});
export const SpaceConfigApplyResponseSchema = z.object({
    applied: z.array(SpaceConfigChangeSchema),
    warnings: z.array(SpaceConfigIssueSchema),
});
